import {
  Button,
  Modal,
  TextField,
  FormLayout,
  Form,
  Checkbox,
  Text,
  Box,
  VerticalStack,
} from '@shopify/polaris';
import { useState, useCallback, useEffect, useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getLeadCenters } from '../../../services/leadsService';
import { getProductCategoriesByGender } from '../../../services/productService';
import { buildCategoryOptions } from '../../../helpers/leadCategoryOptions';
import { togglesToGender } from '../../../helpers/leadGender';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR } from '../../../context/ToastReducer';
import { LeadPhoneField, LeadMultiAutocomplete, LeadGenderChips } from './LeadFields';

const dataFormDefault = {
  name: '',
  surname: '',
  email: '',
  phone: '',
  source: '',
  notes: '',
  consent: false,
};

// Pequeño encabezado de sección, reutilizado en cada bloque del formulario.
const SectionTitle = ({ children }) => (
  <Box paddingBlockEnd="2">
    <Text variant="headingSm" as="h3">
      {children}
    </Text>
  </Box>
);

export const NewLeadModal = ({ onAddLead, disable }) => {
  const [, dispatchToast] = useContext(ToastContext);
  const [active, setActive] = useState(false);
  const handleChange = useCallback(() => setActive((a) => !a), []);

  const [dataForm, setDataForm] = useState(dataFormDefault);
  const [submitting, setSubmitting] = useState(false);

  // Género como toggles independientes (una, las dos o ninguna).
  // Por defecto "ambos" (las dos marcadas) = sin preferencia (se guarda null).
  const [caballero, setCaballero] = useState(true);
  const [dama, setDama] = useState(true);
  // Categorías y centros seleccionados (arrays de value).
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCenters, setSelectedCenters] = useState([]);

  // Centros disponibles (solo se cargan con el modal abierto).
  const { data: centers = [], isLoading: loadingCenters } = useQuery(
    ['leadCenters'],
    () => getLeadCenters().then(({ data }) => data.centers || []),
    {
      enabled: active,
      staleTime: 1000 * 60 * 60,
      onError: (e) => dispatchToast({ type: SET_ERROR, payload: e.response?.data || { message: 'No se pudieron cargar los centros' } }),
    },
  );

  // Categorías de catálogo por género (Hombre/Mujer), cacheadas. Se cargan al
  // abrir el modal; la unión/etiquetado se decide en cliente según el género.
  const { data: catsHombre = [], isLoading: loadingHombre } = useQuery(
    ['leadCategories', 'Hombre'],
    () => getProductCategoriesByGender('Hombre'),
    { enabled: active, staleTime: 1000 * 60 * 60 },
  );
  const { data: catsMujer = [], isLoading: loadingMujer } = useQuery(
    ['leadCategories', 'Mujer'],
    () => getProductCategoriesByGender('Mujer'),
    { enabled: active, staleTime: 1000 * 60 * 60 },
  );

  const categoryOptions = useMemo(
    () => buildCategoryOptions(caballero, dama, catsHombre, catsMujer),
    [caballero, dama, catsHombre, catsMujer],
  );

  // Si una categoría seleccionada deja de estar disponible al cambiar el género,
  // la descartamos para no enviar valores fantasma.
  useEffect(() => {
    const valid = new Set(categoryOptions.map((o) => o.value));
    setSelectedCategories((prev) => prev.filter((c) => valid.has(c)));
  }, [categoryOptions]);

  const centerOptions = useMemo(
    () => centers.map((c) => ({ value: String(c.id), label: `${c.name} (${c.code})` })),
    [centers],
  );

  const resetForm = () => {
    setDataForm(dataFormDefault);
    setCaballero(true);
    setDama(true);
    setSelectedCategories([]);
    setSelectedCenters([]);
  };

  const setField = (field) => (value) => setDataForm((prev) => ({ ...prev, [field]: value }));

  // Una sola marcada → ese género; ambas o ninguna → null (sin preferencia = ambos).
  const interestGender = useMemo(() => togglesToGender(caballero, dama), [caballero, dama]);

  const activator = (
    <Button onClick={handleChange} primary disabled={disable} size="slim">
      Nuevo contacto
    </Button>
  );

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Reglas de validación de cliente (el back valida igualmente).
    if (!dataForm.email.trim() && !dataForm.phone.trim()) {
      dispatchToast({ type: SET_ERROR, payload: { message: 'Indica al menos un email o un teléfono' } });
      return;
    }
    if (selectedCenters.length === 0) {
      dispatchToast({ type: SET_ERROR, payload: { message: 'Selecciona al menos un centro' } });
      return;
    }
    if (!dataForm.consent) {
      dispatchToast({ type: SET_ERROR, payload: { message: 'Debes marcar el consentimiento' } });
      return;
    }

    const payload = {
      name: dataForm.name.trim(),
      surname: dataForm.surname.trim() || undefined,
      email: dataForm.email.trim() || undefined,
      phone: dataForm.phone.trim() || undefined,
      interestGender,
      interestCategory: selectedCategories.length ? selectedCategories.join(':') : undefined,
      source: dataForm.source.trim() || undefined,
      notes: dataForm.notes.trim() || undefined,
      consent: true,
      depositIds: selectedCenters.map((id) => Number(id)),
    };

    setSubmitting(true);
    try {
      await onAddLead(payload);
      resetForm();
      setActive(false);
    } catch (err) {
      // El toast de error ya lo emite el caller; mantenemos el modal abierto.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {activator}
      <Modal
        open={active}
        onClose={handleChange}
        title="Crear nuevo contacto"
        large
        primaryAction={{ content: 'Crear contacto', onAction: handleSubmit, loading: submitting }}
        secondaryActions={[{ content: 'Cancelar', onAction: handleChange }]}
      >
        <Modal.Section>
          <Form onSubmit={handleSubmit}>
            <VerticalStack gap="5">
              <Box>
                <SectionTitle>Identificación</SectionTitle>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Nombre"
                      value={dataForm.name}
                      onChange={setField('name')}
                      autoComplete="off"
                      requiredIndicator
                    />
                    <TextField
                      label="Apellidos"
                      value={dataForm.surname}
                      onChange={setField('surname')}
                      autoComplete="off"
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField
                      type="email"
                      label="Email"
                      value={dataForm.email}
                      onChange={setField('email')}
                      autoComplete="off"
                    />
                    <LeadPhoneField value={dataForm.phone} onChange={setField('phone')} />
                  </FormLayout.Group>
                  <Text variant="bodySm" color="subdued" as="p">
                    Es obligatorio indicar al menos un email o un teléfono.
                  </Text>
                </FormLayout>
              </Box>

              <Box>
                <SectionTitle>Interés y centros</SectionTitle>
                <VerticalStack gap="4">
                  <LeadGenderChips
                    caballero={caballero}
                    dama={dama}
                    onToggleCaballero={() => setCaballero((v) => !v)}
                    onToggleDama={() => setDama((v) => !v)}
                  />
                  <FormLayout>
                    <FormLayout.Group>
                      <LeadMultiAutocomplete
                        label="Categorías de interés"
                        placeholder="Busca y selecciona categorías"
                        options={categoryOptions}
                        selected={selectedCategories}
                        onChange={setSelectedCategories}
                        loading={loadingHombre || loadingMujer}
                        helpText="Cada categoría lleva su género; las compartidas salen una vez por género."
                        emptyMessage="No hay categorías que coincidan"
                      />
                      <LeadMultiAutocomplete
                        label="Centros asociados (al menos uno)"
                        placeholder="Busca y selecciona centros"
                        options={centerOptions}
                        selected={selectedCenters}
                        onChange={setSelectedCenters}
                        loading={loadingCenters}
                        emptyMessage="No hay centros disponibles"
                      />
                    </FormLayout.Group>
                  </FormLayout>
                </VerticalStack>
              </Box>

              <Box>
                <SectionTitle>Origen y observaciones</SectionTitle>
                <FormLayout>
                  <TextField
                    label="¿Cómo nos conoció?"
                    value={dataForm.source}
                    onChange={setField('source')}
                    autoComplete="off"
                    placeholder="Tienda, web, evento…"
                  />
                  <TextField
                    label="Observaciones"
                    value={dataForm.notes}
                    onChange={setField('notes')}
                    autoComplete="off"
                    multiline={3}
                  />
                </FormLayout>
              </Box>

              <Box>
                <SectionTitle>Consentimiento</SectionTitle>
                <Checkbox
                  label="El contacto da su consentimiento para el tratamiento de sus datos"
                  checked={dataForm.consent}
                  onChange={setField('consent')}
                />
              </Box>
            </VerticalStack>
          </Form>
        </Modal.Section>
      </Modal>
    </>
  );
};
